import React, { useState, useEffect } from 'react';
import { FaTrophy, FaCalendar, FaMapMarkerAlt, FaFileAlt, FaChartBar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../services/apiService';
import './HeadToHead.scss';

const HeadToHead = ({ matchDetails, currentTeamName, isActive }) => {
  const [headToHeadData, setHeadToHeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHeadToHeadData = async () => {
      const teamId = sessionStorage.getItem('selected_team_id');
      
      if (!matchDetails?.opposition || !teamId) {
        setLoading(false);
        return;
      }

      try {
        const url = `/get_head_to_head.php?team_id=${teamId}&opposition=${encodeURIComponent(matchDetails.opposition)}`;
        const response = await api.get(url);
        const data = response.data;
        
        if (data.success) {
          setHeadToHeadData(data.data);
        } else {
          console.error('Failed to fetch head to head data:', data.message);
        }
      } catch (error) {
        console.error('Error fetching head to head data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch data when we have the required data, regardless of tab state
    const teamId = sessionStorage.getItem('selected_team_id');
    if (matchDetails?.opposition && teamId) {
      setLoading(true);
      fetchHeadToHeadData();
    } else {
      setLoading(false);
    }
  }, [matchDetails, isActive]);

  const calculateStats = (matches) => {
    if (!matches || matches.length === 0) return null;

    let wins = 0;
    let draws = 0;
    let losses = 0;
    let ourGoals = 0;
    let theirGoals = 0;

    matches.forEach(match => {
      // For academy teams, the scoring system is:
      // - score_white = our team's score (regardless of home/away)
      // - score_colour = opposition's score (regardless of home/away)
      // The is_home field only affects display, not the score assignment
      const ourScore = parseInt(match.score_white || 0);
      const theirScore = parseInt(match.score_colour || 0);

      ourGoals += ourScore;
      theirGoals += theirScore;

      if (ourScore > theirScore) {
        wins++;
      } else if (ourScore === theirScore) {
        draws++;
      } else {
        losses++;
      }
    });

    return {
      total: matches.length,
      wins,
      draws,
      losses,
      ourGoals,
      theirGoals,
      winPercentage: ((wins / matches.length) * 100).toFixed(1)
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatScore = (match) => {
    // For academy teams, the scoring system is:
    // - score_white = our team's score (regardless of home/away)
    // - score_colour = opposition's score (regardless of home/away)
    const ourScore = match.score_white;
    const theirScore = match.score_colour;
    
    return `${ourScore} - ${theirScore}`;
  };

  const getResultClass = (match) => {
    // For academy teams, the scoring system is:
    // - score_white = our team's score (regardless of home/away)
    // - score_colour = opposition's score (regardless of home/away)
    const ourScore = parseInt(match.score_white || 0);
    const theirScore = parseInt(match.score_colour || 0);
    
    if (ourScore > theirScore) return 'win';
    if (ourScore === theirScore) return 'draw';
    return 'loss';
  };

  if (loading) {
    return (
      <div className="head-to-head-container">
        <div className="loading">Loading head to head data...</div>
      </div>
    );
  }

  if (!headToHeadData || !headToHeadData.previousMatches || headToHeadData.previousMatches.length === 0) {
    return (
      <div className="head-to-head-container">
        <div className="no-data">
          <h3>No Previous Meetings</h3>
          <p>This will be the first meeting between {currentTeamName} and {matchDetails?.opposition}.</p>
        </div>
      </div>
    );
  }

  const stats = calculateStats(headToHeadData.previousMatches);

  return (
    <div className="head-to-head-container">
      <h3 className="tab-title">Head to Head</h3>
      
      {/* Overall Statistics */}
      <div className="stats-overview">
        <div className="stats-card">
          <div className="stats-header">
            <FaTrophy className="stats-icon" />
            <h4>Overall Record</h4>
          </div>
          <div className="stats-content">
            <div className="stat-row">
              <span className="stat-label">Total Meetings:</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Wins:</span>
              <span className="stat-value win">{stats.wins}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Draws:</span>
              <span className="stat-value draw">{stats.draws}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Losses:</span>
              <span className="stat-value loss">{stats.losses}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Win Rate:</span>
              <span className="stat-value">{stats.winPercentage}%</span>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-header">
            <FaChartBar className="stats-icon" />
            <h4>Goals</h4>
          </div>
          <div className="stats-content">
            <div className="stat-row">
              <span className="stat-label">{currentTeamName}:</span>
              <span className="stat-value">{stats.ourGoals}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{matchDetails?.opposition}:</span>
              <span className="stat-value">{stats.theirGoals}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Goal Difference:</span>
              <span className={`stat-value ${stats.ourGoals > stats.theirGoals ? 'win' : stats.ourGoals < stats.theirGoals ? 'loss' : 'draw'}`}>
                {stats.ourGoals - stats.theirGoals > 0 ? '+' : ''}{stats.ourGoals - stats.theirGoals}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Infographic */}
      <div className="infographic">
        <h4>Match Results Breakdown</h4>
        <div className="results-chart">
          <div className="chart-bar">
            <div className="bar-section win" style={{ width: `${(stats.wins / stats.total) * 100}%` }}>
              <span className="bar-label">Wins ({stats.wins})</span>
            </div>
            <div className="bar-section draw" style={{ width: `${(stats.draws / stats.total) * 100}%` }}>
              <span className="bar-label">Draws ({stats.draws})</span>
            </div>
            <div className="bar-section loss" style={{ width: `${(stats.losses / stats.total) * 100}%` }}>
              <span className="bar-label">Losses ({stats.losses})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Previous Meetings */}
      <div className="previous-meetings">
        <h4>Previous Meetings</h4>
        <div className="meetings-list">
          {headToHeadData.previousMatches.map((match, index) => (
            <div key={match.match_id} className={`meeting-card ${getResultClass(match)}`}>
              <div className="meeting-header">
                <div className="meeting-date">
                  <FaCalendar className="icon" />
                  {formatDate(match.match_date)}
                </div>
                <div className="meeting-type">
                  {match.match_type}
                </div>
              </div>
              
              <div className="meeting-details">
                <div className="teams">
                  <span className="team home">{currentTeamName}</span>
                  <span className="score">{formatScore(match)}</span>
                  <span className="team away">{matchDetails?.opposition}</span>
                </div>
                
                <div className="match-info">
                  <div className="location">
                    <FaMapMarkerAlt className="icon" />
                    {match.location}
                  </div>
                  <div className="venue">
                    {match.is_home === 1 || match.is_home === '1' ? 'Home' : 'Away'}
                  </div>
                </div>
              </div>

              {match.has_report && (
                <button 
                  className="report-link"
                  onClick={() => navigate(`/match/${match.match_id}/report`)}
                >
                  <FaFileAlt className="icon" />
                  View Match Report
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeadToHead; 